import { useTranslation } from 'react-i18next';
import PageHeader from '@components/page-header/PageHeader';
import SearchInput from '_common/components/search/search-bar/SearchInput';
import usePageView from '_common/hooks/usePageView';
import { PRODUCER_OVERVIEW_PAGE_VIEW } from 'utils/mixpanel';
import { Page } from '@components/page';
import { CommonNavbar } from '@components/navbar';
import { Box } from '@carrot/layout/box';
import { useQueryParams } from '_common/hooks/useQueryParams';
import { useAppSelector } from '_common/hooks/reduxHooks';
import { useMemo, useState } from 'react';
import { ContentLoader } from '@components/content-loader';
import { CustomerList } from '_producer/pages/customer-list-page/customer-list';
import { CustomerTable } from '_producer/pages/customer-list-page/customer-table';
import { useMediaQuery } from '@carrot/utils/media-query';
import clsx from 'clsx';
import { Consumer } from 'types/Consumer';
import { CustomerListEmpty } from '_producer/pages/customer-list-page/empty';
import { AddCustomerButtonLink } from './add-customer-button-link';
import REQ from 'utils/REQ';
import { Container } from '@dagensmat/carrot/Layout';
import {
  FeatureFlags,
  hasUserFeatureFlag
} from '../../../services/FeatureFlagService';

const QUERY_KEY = 'query';

export const CustomerListPage = () => {
  usePageView(PRODUCER_OVERVIEW_PAGE_VIEW);

  const { desktopUp } = useMediaQuery();
  const { getQueryValue, setQueryValue } = useQueryParams();
  const searchQuery = getQueryValue(QUERY_KEY);

  const { consumers, req, featureFlags } = useAppSelector(
    ({ auth, consumers: { items, req } }) => {
      return { consumers: items, req, featureFlags: auth.features };
    }
  );
  const [activeConsumer, setActiveConsumer] = useState<Consumer | null>(null);

  const filteredConsumers = useMemo(() => {
    return consumers
      .filter(consumer => {
        const searchFields = [
          consumer.name,
          ...consumer.emails,
          consumer.phone,
          consumer.contactPerson,
          consumer.deliveryAddress
        ].join(' ');

        return (
          !searchQuery ||
          searchFields.toLowerCase().includes(searchQuery.toLowerCase())
        );
      })
      .sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
  }, [searchQuery, consumers]);

  const { t } = useTranslation();

  if (req !== REQ.SUCCESS) {
    return <Page.Status req={req} />;
  }
  if (consumers.length < 1) {
    return (
      <Page
        header={
          <CommonNavbar showLogo contextName={t('producers:Customers')} />
        }
      >
        <CustomerListEmpty
          title={t('producer:NoCustomersAvailableTitle')}
          description={t('producer:NoCustomersAvailableDescription')}
        />
      </Page>
    );
  }

  return (
    <Page
      header={
        <>
          <CommonNavbar showLogo showActionsOnMobile />
          <Box.FullWidth>
            <Container
              flex
              direction="row"
              justify="space-between"
              alignItems="baseline"
            >
              <PageHeader headerText={t('common:Customer_other')} />
              {hasUserFeatureFlag(
                FeatureFlags.ADD_CUSTOMER_FLOW,
                featureFlags
              ) && (
                <div>
                  <AddCustomerButtonLink />
                </div>
              )}
            </Container>
            <SearchInput
              placeholder={t('producer:SearchCustomerList')}
              value={searchQuery || ''}
              onChange={({ target: { value } }) => {
                return setQueryValue(QUERY_KEY, value);
              }}
              onClear={() => {
                return setQueryValue(QUERY_KEY, '');
              }}
            />
            <div className={clsx('mb-s')}>
              <span className={clsx('text-paragraphSmallBold')}>
                {filteredConsumers.length}
              </span>
              <span className={clsx('text-paragraphSmall')}>
                {' '}
                {t('common:customers')}
              </span>
            </div>
          </Box.FullWidth>
        </>
      }
      noContentPadding
    >
      <ContentLoader req={req}>
        {filteredConsumers.length < 1 ? (
          <CustomerListEmpty
            title={t('producer:CustomerListNoResultsTitle')}
            description={t('producer:CustomerListNoResultsDescription')}
          />
        ) : desktopUp ? (
          <CustomerTable
            consumers={filteredConsumers}
            activeConsumer={activeConsumer}
            onConsumerClick={consumer => setActiveConsumer(consumer)}
            onModalClose={() => setActiveConsumer(null)}
          />
        ) : (
          <CustomerList
            consumers={filteredConsumers}
            activeConsumer={activeConsumer}
            onConsumerClick={consumer => setActiveConsumer(consumer)}
            onModalClose={() => setActiveConsumer(null)}
          />
        )}
      </ContentLoader>
    </Page>
  );
};
